import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid, Checkbox, FormControlLabel } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydButton from '../../../../commonDesign/Button'
import Switch from '../../../../molecules/Switch'
import StyleVariantPicker from '../../../../pages/denali/Analysis/QueryBlueprints/components/presentationManagement/typeEditors/StyleVariantPicker'
import generateLocalStorageKey from '../../../../pages/denali/Analysis/QueryBlueprints/components/presentationManagement/generateLocalStorageKey'
import {
  INCEPTION_VALUE_OPTIONS,
  QUICK_DATE_OPTIONS,
  ROW_PRESET_OPTIONS,
  REPORT_CATEGORY_OPTIONS
} from './constants'
import { buildDefaultRows } from './presets'
import RowEditor from './RowEditor'

const quickDateChoices = QUICK_DATE_OPTIONS.filter(o => o.value !== 'Custom')

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function SwitchField ({ name, control, label }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
          <Switch
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
          />
        </div>
      )}
    />
  )
}

SwitchField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired
}

function ActivitySummaryConfigEditor ({ fieldPrefix }) {
  const { control, setValue } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`
  const [presetValue, setPresetValue] = useState('standard')

  const handleApplyPreset = useCallback(() => {
    setValue(`${configPrefix}.rows`, buildDefaultRows(presetValue))
  }, [configPrefix, setValue, presetValue])

  const handleRowsChange = useCallback((rows) => {
    setValue(`${configPrefix}.rows`, rows)
  }, [configPrefix, setValue])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Display</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' helperText='Header text shown above the table'>
              <SydInput
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Rows</div>
      </Grid>
      <Grid item xs={12}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '8px' }}>
          <SydLabel label='Load Preset' helperText='Apply a preset template to the row list'>
            <SydSelect
              options={ROW_PRESET_OPTIONS}
              value={presetValue}
              onChange={(e) => setPresetValue(e.target.value)}
              size='sm'
              style={{ minWidth: '160px' }}
            />
          </SydLabel>
          <SydButton
            variant='outline'
            size='sm'
            onClick={handleApplyPreset}
          >
            Apply
          </SydButton>
        </div>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.rows`}
          control={control}
          render={({ field }) => (
            <RowEditor
              rows={field.value || []}
              onChange={handleRowsChange}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Date Range</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.dateRanges.default.quickDate`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Default Date Range' helperText='Quick date preset for the initial view'>
              <SydSelect
                options={quickDateChoices}
                value={field.value || 'YTD'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.availableQuickDates`}
          control={control}
          render={({ field }) => {
            const selected = new Set(field.value ?? [])
            const allSelected = field.value == null
            const handleToggleAll = () => {
              field.onChange(allSelected ? quickDateChoices.map(o => o.value) : null)
            }
            const handleToggle = (val) => {
              const base = allSelected ? quickDateChoices.map(o => o.value) : [...selected]
              const next = base.includes(val) ? base.filter(v => v !== val) : [...base, val]
              field.onChange(next.length === quickDateChoices.length ? null : next)
            }
            return (
              <SydLabel label='Available Quick Dates' helperText='Which date options are available to end users (all if none selected)'>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
                  <FormControlLabel
                    control={<Checkbox checked={allSelected} onChange={handleToggleAll} size='small' />}
                    label={<span style={{ fontSize: 'var(--font-size-sm)' }}>All</span>}
                  />
                  {quickDateChoices.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          checked={allSelected || selected.has(opt.value)}
                          onChange={() => handleToggle(opt.value)}
                          size='small'
                        />
                      }
                      label={<span style={{ fontSize: 'var(--font-size-sm)' }}>{opt.label}</span>}
                    />
                  ))}
                </div>
              </SydLabel>
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Valuation</div>
      </Grid>
      <Grid item xs={6}>
        <SwitchField
          name={`${configPrefix}.bondAccrual`}
          control={control}
          label='Adjust BMV/EMV for Bond Accrual'
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.inceptionValueMethod`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Inception Value Method' helperText='How inception values are calculated'>
              <SydSelect
                options={INCEPTION_VALUE_OPTIONS}
                value={field.value || 'New Money'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Behavior</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.reportCategoryId`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Report Category' helperText='Activity summary scope'>
              <SydSelect
                options={REPORT_CATEGORY_OPTIONS}
                value={field.value ?? 2}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <SwitchField
          name={`${configPrefix}.showDrillDown`}
          control={control}
          label='Enable Drill-Down'
        />
      </Grid>
      <Grid item xs={6}>
        <SwitchField
          name={`${configPrefix}.showDateControls`}
          control={control}
          label='Allow users to change date range'
        />
      </Grid>
      <Grid item xs={6}>
        <SwitchField
          name={`${configPrefix}.allowCustomDates`}
          control={control}
          label='Allow custom date range'
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Formatting</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.valueFormat`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Value Format' helperText='Format key: marketValue, currency, money, number, etc.'>
              <SydInput
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder='marketValue'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.performanceFormat`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Performance Format' helperText='Format key: returns, percentage, allocation, etc.'>
              <SydInput
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder='returns'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Persistence</div>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.localStorageKey`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Local Storage Key' helperText='Unique key for persisting user date selections'>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <SydInput
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder='Auto-generated on creation'
                />
                <SydButton
                  size='sm'
                  variant='outline'
                  onClick={() => field.onChange(generateLocalStorageKey())}
                  disabled={!!field.value}
                >
                  Auto
                </SydButton>
                {field.value
                  ? (
                    <SydButton
                      size='sm'
                      variant='ghost'
                      onClick={() => field.onChange('')}
                    >
                      Clear
                    </SydButton>
                  )
                  : null}
              </div>
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Table Style</div>
      </Grid>
      <Controller
        name={`${configPrefix}.tableStyleVariant`}
        control={control}
        render={({ field: variantField }) => (
          <Controller
            name={`${configPrefix}.tableStyleVariantParams`}
            control={control}
            render={({ field: paramsField }) => (
              <StyleVariantPicker
                presentationType='Presentation Table'
                value={variantField.value || 'default'}
                onChange={(v) => {
                  variantField.onChange(v)
                  paramsField.onChange({})
                }}
                variantParams={paramsField.value || {}}
                onParamsChange={(p) => paramsField.onChange(p)}
              />
            )}
          />
        )}
      />
    </Grid>
  )
}

ActivitySummaryConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default ActivitySummaryConfigEditor
